import React, { useEffect, useState } from 'react';
import '../StylesComponents/design-system.css';

function MediaKitPage() {
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    // Load html2canvas and jspdf from CDN
    const scripts = [
      "https://html2canvas.hertzen.com/dist/html2canvas.min.js",
      "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"
    ];

    scripts.forEach(src => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    });
  }, []);

  const handleDownloadPDF = async () => {
    if (!window.html2canvas || !window.jspdf) {
      alert("Loading PDF engine... Please try again in 1 second.");
      return;
    }

    setIsGenerating(true);
    try {
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF('l', 'mm', 'a4');
      const sections = document.querySelectorAll('.pdf-section');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < sections.length; i++) {
        const canvas = await window.html2canvas(sections[i], {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: 1400 // Ensure consistent width for capture
        });

        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const imgProps = pdf.getImageProperties(imgData);
        
        // Calculate dimensions to fit the page while maintaining aspect ratio
        const displayWidth = pdfWidth - 20; // 10mm margin on each side
        const displayHeight = (imgProps.height * displayWidth) / imgProps.width;

        // If it's not the first page, add a new page
        if (i > 0) {
          pdf.addPage('l', 'mm', 'a4');
        }

        // Center vertically if it's shorter than the page
        const yOffset = displayHeight < pdfHeight ? (pdfHeight - displayHeight) / 2 : 10;
        
        pdf.addImage(imgData, 'JPEG', 10, yOffset, displayWidth, displayHeight);
      }

      pdf.save(`stockroom-media-kit-2026.pdf`);
    } catch (error) {
      console.error("PDF_ERROR:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const stats = [
    { label: 'AVERAGE_SESSION_DURATION', value: '4M 32S', trend: 'HIGH_ENGAGEMENT' },
    { label: 'AUDIENCE_DEMOGRAPHIC', value: '18-35', trend: 'TECH_SAVVY_LUXURY' },
    { label: 'MARKET_SYNC_RATE', value: 'REAL-TIME', trend: 'AUTOMATED_MANIFEST' }
  ];

  const promotionalTemplates = [
    { id: '1', name: 'TECHNICAL_BRUTALIST' },
    { id: '2', name: 'EDITORIAL_MANIFESTO' },
    { id: '3', name: 'NOIR_DUALITY' },
    { id: '4', name: 'CYBER_SCAN' }
  ];

  return (
    <div style={{ background: 'white' }}>
      {/* PDF Download Button */}
      <button 
        onClick={handleDownloadPDF}
        disabled={isGenerating}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 25px',
          background: 'black',
          color: 'white',
          border: 'none',
          fontFamily: 'monospace',
          fontSize: '12px',
          fontWeight: '900',
          cursor: 'pointer',
          zIndex: 9999,
        }}
        className="no-print"
      >
        {isGenerating ? 'GENERATING_PDF...' : 'DOWNLOAD_PDF_LANDSCAPE'}
      </button>

      <style>
        {`
          @media print {
            header, footer, nav, .no-print, .mobile-nav-bar, .sub-header-container, .header-container {
              display: none !important;
            }
            body, html {
              background: white !important;
              color: black !important;
            }
            .media-kit-container {
              padding: 40px !important;
              margin: 0 !important;
              max-width: none !important;
            }
          }
        `}
      </style>

      <div className="media-kit-container" style={{ padding: '28px 20px', color: 'black', background: 'white', minHeight: '100vh', maxWidth: '1400px', margin: '0 auto', fontFamily: 'var(--font-primary)' }}>

      {/* HEADER SECTION */}
      <header className="pdf-section" style={{ marginBottom: '80px', borderBottom: '1px solid var(--accent-primary)', padding: '40px 0 60px' }}>
        <div style={{ color: 'var(--accent-primary)', fontSize: '12px', marginBottom: '15px', fontFamily: 'monospace', letterSpacing: '2px' }}>
          PARTNERSHIP_MANIFESTO // RAKUTEN_ADVERTISING_2026
        </div>
        <h1 style={{ fontSize: 'clamp(3.5rem, 12vw, 7rem)', fontWeight: '900', letterSpacing: '-4px', lineHeight: '0.85', marginBottom: '20px', textTransform: 'uppercase', color: 'black' }}>
          STOCK<span style={{ color: 'var(--accent-primary)' }}>ROOM</span> <br />MEDIA_KIT
        </h1>
        <p style={{ fontSize: '1.5rem', fontWeight: '500', maxWidth: '700px', lineHeight: '1.3', marginTop: '30px', color: 'black' }}>
          A technical, brutalist-inspired fashion marketplace and market aggregator strictly curating discounted products from the Australian market.
        </p>
      </header>

      {/* CORE STATS GRID */}
      <section className="pdf-section" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '80px', padding: '20px 0' }}>
        {stats.map((stat, index) => (
          <div key={index} style={{ border: '1px solid #eee', padding: '30px', background: '#f9f9f9' }}>
            <div style={{ fontSize: '10px', color: '#666', marginBottom: '15px', fontFamily: 'monospace' }}>{stat.label}</div>
            <div style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '5px', color: 'black' }}>{stat.value}</div>
            <div style={{ fontSize: '11px', color: 'var(--accent-primary)', fontFamily: 'monospace' }}>{stat.trend}</div>
          </div>
        ))}
      </section>

      {/* BRAND OVERVIEW */}
      <section className="pdf-section" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '60px', marginBottom: '100px', padding: '40px 0' }}>
        <div>
          <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '30px', fontFamily: 'monospace' }}>01 // THE_CONCEPT</h3>
          <p style={{ fontSize: '1.25rem', lineHeight: '1.6', color: 'black' }}>
            Stockroom is not just a marketplace; it's a technical ecosystem. We strictly curate the Australian fashion market to exclusively feature discounted products within a single, high-performance manifest. 
            Our interface is built for the modern consumer who values speed, precision, and authenticity over traditional retail fluff.
          </p>
        </div>
        <div>
          <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '30px', fontFamily: 'monospace' }}>02 // WHY_RAKUTEN</h3>
          <p style={{ fontSize: '1.25rem', lineHeight: '1.6', color: 'black' }}>
            Our infrastructure is designed for high-scale affiliate integration. By partnering with Rakuten Advertising, we aim to bridge the gap between premium brands and a highly targeted, tech-savvy audience that actively tracks luxury market trends.
          </p>
        </div>
      </section>

      {/* TECHNICAL INFRASTRUCTURE */}
      <section className="pdf-section" style={{ background: 'transparent', padding: '60px 40px', border: '1px solid #1a1a1a', marginBottom: '80px' }}>
        <h2 style={{ fontSize: '2.5rem', fontWeight: '900', marginBottom: '40px', letterSpacing: '-1px', color: 'black' }}>TECHNICAL_INFRASTRUCTURE</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '30px' }}>
          <div>
            <h4 style={{ fontSize: '14px', color: 'black', marginBottom: '10px' }}>REAL-TIME SYNC</h4>
            <p style={{ fontSize: '0.9rem', color: 'black' }}>Automated manifest updates ensuring product availability and price accuracy across all brand partners.</p>
          </div>
          <div>
            <h4 style={{ fontSize: '14px', color: 'black', marginBottom: '10px' }}>PROMOTIONAL MARKETING</h4>
            <p style={{ fontSize: '0.9rem', color: 'black' }}>A qualified promotional marketing team focused on strategic asset distribution and high-performance marketing campaigns.</p>
          </div>
          <div>
            <h4 style={{ fontSize: '14px', color: 'black', marginBottom: '10px' }}>SECURE ARCHIVE</h4>
            <p style={{ fontSize: '0.9rem', color: 'black' }}>Hardened backend with RBAC and NoSQL protection, ensuring a premium environment for luxury brands.</p>
          </div>
        </div>
      </section>

      {/* ADVERTISING ASSETS SHOWCASE */}
      <section className="pdf-section" style={{ marginBottom: '100px', padding: '20px 0' }}>
        <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '40px', fontFamily: 'monospace' }}>03 // PARTNERSHIP_PLACEMENTS</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          {['FEATURED_BANNER', 'TRENDING_CAROUSEL', 'BRAND_MANIFEST', 'CURATED_FEED'].map((slot, i) => (
            <div key={i} style={{ padding: '15px 30px', border: '1px solid #333', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', color: 'black' }}>
              {slot}
            </div>
          ))}
        </div>
      </section>

      {/* PROMOTIONAL MATERIAL SHOWCASE - Template Breakdown */}
      {promotionalTemplates.map((template, index) => (
        <section key={template.id} className="pdf-section" style={{ marginBottom: '100px', padding: '40px 0' }}>
          {index === 0 && (
            <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '40px', fontFamily: 'monospace' }}>04 // PROMOTIONAL_MATERIAL</h3>
          )}
          <div>
            <h4 style={{ fontSize: '12px', color: '#666', marginBottom: '20px', fontFamily: 'monospace', textAlign: 'center' }}>
              TEMPLATE_{template.id.padStart(2, '0')} // {template.name}
            </h4>
            <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-end', justifyContent: 'center', overflowX: 'auto', paddingBottom: '20px' }}>
              {/* Desktop View */}
              <div style={{ flex: '0 0 auto' }}>
                <div style={{ fontSize: '10px', color: '#999', marginBottom: '10px', fontFamily: 'monospace' }}>DESKTOP_VIEW_1920x1080</div>
                <img 
                  src={`/images/mediakit/mediakitdesktop${template.id}.png`} 
                  alt={`${template.name} Desktop`} 
                  style={{ width: '850px', height: 'auto', border: '1px solid #eee', display: 'block' }}
                />
              </div>
              {/* Mobile View */}
              <div style={{ flex: '0 0 auto' }}>
                <div style={{ fontSize: '10px', color: '#999', marginBottom: '10px', fontFamily: 'monospace' }}>MOBILE_VIEW_1080x1350</div>
                <img 
                  src={`/images/mediakit/mediakitmobile${template.id}.png`} 
                  alt={`${template.name} Mobile`} 
                  style={{ width: '320px', height: 'auto', border: '1px solid #eee', display: 'block' }}
                />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* VISUAL SYSTEM */}
      <section className="pdf-section" style={{ marginBottom: '100px', padding: '40px 0' }}>
        <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '40px', fontFamily: 'monospace' }}>05 // VISUAL_SYSTEM</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px' }}>
          {/* Colours */}
          <div style={{ border: '1px solid #eee', padding: '30px' }}>
            <h4 style={{ fontSize: '12px', color: '#666', marginBottom: '20px', fontFamily: 'monospace' }}>COLOUR_PALETTE</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ width: '40px', height: '40px', background: '#00E676', border: '1px solid #ddd' }}></div>
                <div><div style={{ fontSize: '12px', fontWeight: '800' }}>ELECTRIC_EMERALD</div><div style={{ fontSize: '10px', color: '#999' }}>#00E676 (ACCENT)</div></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ width: '40px', height: '40px', background: '#000000', border: '1px solid #ddd' }}></div>
                <div><div style={{ fontSize: '12px', fontWeight: '800' }}>PURE_BLACK</div><div style={{ fontSize: '10px', color: '#999' }}>#000000 (PRIMARY)</div></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ width: '40px', height: '40px', background: '#FFFFFF', border: '1px solid #ddd' }}></div>
                <div><div style={{ fontSize: '12px', fontWeight: '800' }}>PURE_WHITE</div><div style={{ fontSize: '10px', color: '#999' }}>#FFFFFF (BASE)</div></div>
              </div>
            </div>
          </div>
          {/* Typography */}
          <div style={{ border: '1px solid #eee', padding: '30px' }}>
            <h4 style={{ fontSize: '12px', color: '#666', marginBottom: '20px', fontFamily: 'monospace' }}>TYPOGRAPHY</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '18px', fontWeight: '900', fontFamily: 'Montserrat' }}>MONTSERRAT</div>
                <div style={{ fontSize: '10px', color: '#999' }}>PRIMARY_HEADINGS // 900 WEIGHT</div>
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: '800', fontFamily: 'Syne' }}>SYNE</div>
                <div style={{ fontSize: '10px', color: '#999' }}>ACCENT_STATEMENTS // 800 WEIGHT</div>
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '400', fontFamily: 'monospace' }}>ROBOTO MONO</div>
                <div style={{ fontSize: '10px', color: '#999' }}>TECHNICAL_DATA // DATA_LABELS</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OPERATIONAL STANDARDS & AUDIENCE PROFILE */}
      <section className="pdf-section" style={{ marginBottom: '100px', borderTop: '1px solid #eee', padding: '60px 0' }}>
        <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '40px', fontFamily: 'monospace' }}>06 // OPERATIONAL_STANDARDS</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '30px', marginBottom: '80px' }}>
          <div>
            <h5 style={{ fontSize: '12px', fontWeight: '800', marginBottom: '10px' }}>STRICT CURATION</h5>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.4' }}>Every product is manually verified for discount accuracy and brand authenticity before entering the manifest.</p>
          </div>
          <div>
            <h5 style={{ fontSize: '12px', fontWeight: '800', marginBottom: '10px' }}>LINK INTEGRITY</h5>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.4' }}>Affiliate links are hardened via server-side redirection to ensure 100% attribution accuracy and secure user paths.</p>
          </div>
          <div>
            <h5 style={{ fontSize: '12px', fontWeight: '800', marginBottom: '10px' }}>BRAND SAFETY</h5>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.4' }}>Stockroom maintains a "No Noise" policy, ensuring premium brands are never displayed alongside non-vetted or low-quality retailers.</p>
          </div>
        </div>

        <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '40px', fontFamily: 'monospace' }}>07 // AUDIENCE_PROFILE</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px' }}>
          <div style={{ borderLeft: '2px solid black', paddingLeft: '25px' }}>
            <h5 style={{ fontSize: '12px', fontWeight: '800', marginBottom: '10px' }}>ARCHIVE_ENTHUSIASTS</h5>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.4' }}>High-value collectors tracking specific seasons and "grail" items within the Australian market.</p>
          </div>
          <div style={{ borderLeft: '2px solid black', paddingLeft: '25px' }}>
            <h5 style={{ fontSize: '12px', fontWeight: '800', marginBottom: '10px' }}>EFFICIENCY_SEEKERS</h5>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.4' }}>Professionals who value our technical interface for rapid market cross-referencing and immediate acquisition.</p>
          </div>
          <div style={{ borderLeft: '2px solid black', paddingLeft: '25px' }}>
            <h5 style={{ fontSize: '12px', fontWeight: '800', marginBottom: '10px' }}>TECH-WEAR_COMMUNITY</h5>
            <p style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.4' }}>A growing sub-sector focused on performance materials and technical aesthetics aligned with our brutalist UI.</p>
          </div>
        </div>
      </section>

      {/* ASSET SPECIFICATIONS & ONBOARDING */}
      <section className="pdf-section" style={{ marginBottom: '100px', background: '#f9f9f9', padding: '60px', border: '1px solid #eee' }}>
        <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '40px', fontFamily: 'monospace' }}>08 // ASSET_SPECIFICATIONS</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px', marginBottom: '60px' }}>
          <div>
            <div style={{ fontSize: '10px', color: '#999', marginBottom: '5px', fontFamily: 'monospace' }}>HERO_BANNERS</div>
            <div style={{ fontSize: '14px', fontWeight: '900' }}>1920 x 1080 PX</div>
            <div style={{ fontSize: '10px', color: '#666' }}>PNG / WebP (MAX 2MB)</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: '#999', marginBottom: '5px', fontFamily: 'monospace' }}>MOBILE_PLACEMENTS</div>
            <div style={{ fontSize: '14px', fontWeight: '900' }}>1080 x 1350 PX</div>
            <div style={{ fontSize: '10px', color: '#666' }}>OPTIMISED FOR RETINA</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: '#999', marginBottom: '5px', fontFamily: 'monospace' }}>BRAND_LOGOS</div>
            <div style={{ fontSize: '14px', fontWeight: '900' }}>SVG_ONLY</div>
            <div style={{ fontSize: '10px', color: '#666' }}>MONOCHROME_REQUIRED</div>
          </div>
        </div>

        <h3 style={{ fontSize: '14px', letterSpacing: '3px', color: 'var(--accent-primary)', marginBottom: '40px', fontFamily: 'monospace' }}>09 // ONBOARDING_SEQUENCE</h3>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <div style={{ flex: 1, padding: '20px', border: '1px solid #eee', background: 'white', textAlign: 'center' }}>
            <div style={{ color: 'var(--accent-primary)', fontWeight: '900', fontSize: '24px' }}>01</div>
            <div style={{ fontSize: '10px', fontWeight: '800' }}>SIGNAL_ACQUISITION</div>
            <div style={{ fontSize: '10px', color: '#999' }}>Initial contact & goal alignment</div>
          </div>
          <div style={{ color: '#ccc', fontSize: '20px' }}>→</div>
          <div style={{ flex: 1, padding: '20px', border: '1px solid #eee', background: 'white', textAlign: 'center' }}>
            <div style={{ color: 'var(--accent-primary)', fontWeight: '900', fontSize: '24px' }}>02</div>
            <div style={{ fontSize: '10px', fontWeight: '800' }}>MANIFEST_INTEGRATION</div>
            <div style={{ fontSize: '10px', color: '#999' }}>Technical feed sync & testing</div>
          </div>
          <div style={{ color: '#ccc', fontSize: '20px' }}>→</div>
          <div style={{ flex: 1, padding: '20px', border: '1px solid #eee', background: 'white', textAlign: 'center' }}>
            <div style={{ color: 'var(--accent-primary)', fontWeight: '900', fontSize: '24px' }}>03</div>
            <div style={{ fontSize: '10px', fontWeight: '800' }}>LIVE_DEPLOYMENT</div>
            <div style={{ fontSize: '10px', color: '#999' }}>Full market visibility & tracking</div>
          </div>
        </div>
      </section>

      {/* FOOTER / CONTACT */}
      <footer className="pdf-section" style={{ borderTop: '1px solid #1a1a1a', padding: '60px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ fontSize: '2rem', fontWeight: '900', marginBottom: '10px', color: 'black' }}>READY FOR_DEPLOYMENT.</h2>
          <p style={{ color: 'black', fontFamily: 'monospace' }}>CONTACT // JAYDEN@CURATEDCOLLECTIVE.CO</p>
        </div>
        <div style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: '10px', color: 'black' }}>
          SYSTEM_VERSION // 1.0.4 <br />
          BUILD_DATE // 2026.03.04
        </div>
      </footer>
    </div>
    </div>
  );
}

export default MediaKitPage;
